import React, { useMemo, useRef } from 'react'
import { OrdinalWalletBridgeProps, useOrdinalWalletBridge } from './useOrdinalWalletBridge'

interface HTMLOrdinalRenderProps {
    src: string
    className?: string
    allowInteraction?: boolean
    title?: string
    htmlAlign?: 'center' | 'top-center'
    // Gives the running ordinal access to the viewer's wallet (see useOrdinalWalletBridge).
    walletBridge?: OrdinalWalletBridgeProps | null
}

// Counts loads of the current src on the element. A second load means the page inside the
// frame navigated, and the wallet bridge stops answering it (see ordinalWalletBridge/host).
const countLoads = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = event.currentTarget
    const src = iframe.getAttribute('src') || ''
    if (iframe.dataset.bridgeSrc !== src) {
        iframe.dataset.bridgeSrc = src
        iframe.dataset.bridgeLoads = '1'
    } else {
        iframe.dataset.bridgeLoads = String(Number(iframe.dataset.bridgeLoads || 0) + 1)
    }
}

// Mirrors Ordinals::UrlPreview::CONTENT_HOSTS: third-party hosts that serve raw ordinal content.
const TRUSTED_SAME_ORIGIN_HOSTS = new Set([
    'https://ordinals.gorillapool.io',
    'https://ordfs.network',
])

const HTMLOrdinalRender: React.FC<HTMLOrdinalRenderProps> = ({
    src,
    className,
    allowInteraction = true,
    title = 'HTML Preview',
    htmlAlign = 'top-center',
    walletBridge,
}) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    useOrdinalWalletBridge(iframeRef, walletBridge, src)

    /**
     * Security:
     * - We intentionally avoid `srcDoc` entirely (no fetched/inlined HTML) so the iframe content
     *   remains a true separate origin (e.g. `ordfs.network`) and cannot access the parent context.
     */
    // `allow-same-origin` is only safe for the known cross-origin content host. Anything else
    // (notably our own API origin, which serves user HTML) must stay in an opaque origin.
    const sandbox = useMemo(() => {
        let trusted = false
        try {
            trusted = TRUSTED_SAME_ORIGIN_HOSTS.has(new URL(src).origin)
        } catch {
            trusted = false
        }
        return trusted ? 'allow-scripts allow-same-origin' : 'allow-scripts'
    }, [src])

    const wrapperContainment = `
<style>
  html,body{height:100%;margin:0}
  body{display:flex;align-items:center;justify-content:center;overflow:hidden}
  img,video,canvas,svg,object,embed,iframe{max-width:100%;max-height:100%}
</style>`
    const htmlContainment = useMemo(() => {
        if (htmlAlign === 'top-center') {
            return `
<style>
  html,body{height:100%;margin:0}
  body{display:flex;align-items:flex-start;justify-content:center;overflow:hidden}
  img,video,canvas,svg,object,embed,iframe{max-width:100%;max-height:100%}
</style>`
        }
        return wrapperContainment
    }, [htmlAlign])

    return (
        <div className={className} style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <iframe
                ref={iframeRef}
                // A bridged frame gets its src from useOrdinalWalletBridge, once it holds the wallet.
                src={walletBridge?.origin ? undefined : src}
                title={title}
                style={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                    pointerEvents: allowInteraction ? 'auto' : 'none',
                    cursor: allowInteraction ? 'auto' : 'default',
                }}
                loading="lazy"
                referrerPolicy="no-referrer"
                sandbox={sandbox}
                scrolling="auto"
                allow="fullscreen"
                onLoad={countLoads}
            />
        </div>
    )
}

export default React.memo(HTMLOrdinalRender)
